/**
 * Self-Referral Form Validation Utilities
 *
 * Validation logic specific to self-referral form data.
 * Leverages existing validation utilities and adds referral-specific rules.
 *
 * @module utils/validation/referralValidation
 */

import type {
  SelfReferralFormData,
  ValidationResult,
  ValidationErrors
} from '@/components/self-referral/types';
import type { ValidationResult as BaseValidationResult } from '@/types/registration';
import { validateEmail } from './emailValidation';
import { validatePhoneNumber } from './phoneValidation';
import { isValidHealthCondition } from '@/constants/healthConditions';

/**
 * Self-referral specific validation error messages
 */
const REFERRAL_ERRORS = {
  NAME_TOO_SHORT: 'Name must be at least 2 characters long',
  NAME_TOO_LONG: 'Name must be no more than 100 characters long',
  NAME_INVALID_CHARS: 'Name can only contain letters, spaces, hyphens, and apostrophes',
  INVALID_DATE: 'Please enter a valid date in YYYY-MM-DD format',
  INVALID_DATE_RANGE: 'Date must be between 1900 and current year',
  FUTURE_DATE: 'Date of birth cannot be in the future',
  INVALID_AGE: 'Age must be between 0 and 120 years',
  MINIMUM_AGE_REQUIRED: 'You must be at least 13 years old to use this service',
  CONDITIONS_REQUIRED: 'Please select at least one health condition',
  CONDITIONS_TOO_MANY: 'Please select no more than 10 health conditions',
  INVALID_CONDITION: 'One or more selected conditions are invalid',
  NOTES_TOO_LONG: 'Additional notes must be no more than 1000 characters long',
  NOTES_INVALID_CHARS: 'Additional notes contain invalid characters'
} as const;

/**
 * Name validation regex pattern
 * Allows letters, spaces, hyphens, apostrophes, and common international characters
 */
const NAME_REGEX = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s'-]+$/;

/**
 * Date validation regex pattern (YYYY-MM-DD format)
 */
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Convert base ValidationResult to referral ValidationResult format
 */
const convertValidationResult = (result: BaseValidationResult): string[] => {
  return result.isValid ? [] : [result.error || 'Validation failed'];
};

/**
 * Validate patient name (first or last name)
 */
export const validatePatientName = (name: string): string[] => {
  const errors: string[] = [];

  if (!name || name.trim().length === 0) {
    errors.push('This field is required');
    return errors;
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    errors.push(REFERRAL_ERRORS.NAME_TOO_SHORT);
  }

  if (trimmedName.length > 100) {
    errors.push(REFERRAL_ERRORS.NAME_TOO_LONG);
  }

  if (!NAME_REGEX.test(trimmedName)) {
    errors.push(REFERRAL_ERRORS.NAME_INVALID_CHARS);
  }

  return errors;
};

/**
 * Validate email using existing email validation
 */
export const validateReferralEmail = (email: string): string[] => {
  const result = validateEmail(email);
  return convertValidationResult(result);
};

/**
 * Validate phone number using existing phone validation
 */
export const validateReferralPhone = (phone?: { countryCode: string; isoCode: string; number: string }): string[] => {
  // Phone is optional for referrals
  if (!phone || !phone.number || phone.number.trim().length === 0) {
    return [];
  }

  try {
    const result = validatePhoneNumber({
      countryCode: phone.countryCode,
      isoCode: phone.isoCode,
      number: phone.number,
      formatted: phone.number,
      international: `+${phone.countryCode}${phone.number}`
    });

    return convertValidationResult(result);
  } catch {
    return ['Please enter a valid phone number'];
  }
};

/**
 * Validate date of birth (optional field)
 */
export const validateDateOfBirth = (dateOfBirth?: string): string[] => {
  const errors: string[] = [];

  // Date of birth is optional, so empty is valid
  if (!dateOfBirth || dateOfBirth.trim().length === 0) {
    return errors;
  }

  const trimmedDate = dateOfBirth.trim();

  if (!DATE_REGEX.test(trimmedDate)) {
    errors.push(REFERRAL_ERRORS.INVALID_DATE);
    return errors;
  }

  try {
    const date = new Date(trimmedDate);
    const currentDate = new Date();

    // Check if date is valid
    if (isNaN(date.getTime())) {
      errors.push(REFERRAL_ERRORS.INVALID_DATE);
      return errors;
    }

    // Check if date is in the future
    if (date > currentDate) {
      errors.push(REFERRAL_ERRORS.FUTURE_DATE);
    }

    // Check reasonable date range (1900 to current year)
    const year = date.getFullYear();
    const currentYear = currentDate.getFullYear();

    if (year < 1900 || year > currentYear) {
      errors.push(REFERRAL_ERRORS.INVALID_DATE_RANGE);
    }

    // Check reasonable age range (0 to 120 years)
    const age = currentYear - year;
    if (age < 0 || age > 120) {
      errors.push(REFERRAL_ERRORS.INVALID_AGE);
    }

    // Check minimum age requirement (13 years old)
    // Calculate more precise age considering month and day
    const today = new Date();
    const birthDate = new Date(date);
    let preciseAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      preciseAge--;
    }

    if (preciseAge < 13) {
      errors.push(REFERRAL_ERRORS.MINIMUM_AGE_REQUIRED);
    }

  } catch {
    errors.push(REFERRAL_ERRORS.INVALID_DATE);
  }

  return errors;
};

/**
 * Validate health conditions array
 */
export const validateHealthConditions = (conditions: string[]): string[] => {
  const errors: string[] = [];

  if (!conditions || conditions.length === 0) {
    errors.push(REFERRAL_ERRORS.CONDITIONS_REQUIRED);
    return errors;
  }

  if (conditions.length > 10) {
    errors.push(REFERRAL_ERRORS.CONDITIONS_TOO_MANY);
  }

  // Check if all conditions are valid
  const invalidConditions = conditions.filter(condition => !isValidHealthCondition(condition));
  if (invalidConditions.length > 0) {
    errors.push(REFERRAL_ERRORS.INVALID_CONDITION);
  }

  return errors;
};

/**
 * Validate additional notes (optional field)
 */
export const validateAdditionalNotes = (notes?: string): string[] => {
  const errors: string[] = [];

  // Additional notes are optional, so empty is valid
  if (!notes || notes.trim().length === 0) {
    return errors;
  }

  const trimmedNotes = notes.trim();

  if (trimmedNotes.length > 1000) {
    errors.push(REFERRAL_ERRORS.NOTES_TOO_LONG);
  }

  // Check for potentially harmful characters (basic XSS prevention)
  const dangerousChars = /<script|javascript:|data:|vbscript:|onload=|onerror=/i;
  if (dangerousChars.test(trimmedNotes)) {
    errors.push(REFERRAL_ERRORS.NOTES_INVALID_CHARS);
  }

  return errors;
};

/**
 * Validate individual form field
 */
export const validateField = (
  fieldName: keyof SelfReferralFormData,
  value: unknown
): string[] => {
  switch (fieldName) {
    case 'first_name':
    case 'last_name':
      return validatePatientName(value as string);

    case 'email':
      return validateReferralEmail(value as string);

    case 'phone':
      return validateReferralPhone(value as { countryCode: string; isoCode: string; number: string } | undefined);

    case 'date_of_birth':
      return validateDateOfBirth(value as string | undefined);

    case 'conditions':
      return validateHealthConditions(value as string[]);

    case 'additional_notes':
      return validateAdditionalNotes(value as string | undefined);

    default:
      return [];
  }
};

/**
 * Validate complete self-referral form data
 */
export const validateSelfReferralForm = (formData: SelfReferralFormData): ValidationResult => {
  const errors: ValidationErrors = {};

  // Validate each field
  const firstNameErrors = validatePatientName(formData.first_name);
  if (firstNameErrors.length > 0) {
    errors.first_name = firstNameErrors;
  }

  const lastNameErrors = validatePatientName(formData.last_name);
  if (lastNameErrors.length > 0) {
    errors.last_name = lastNameErrors;
  }

  const emailErrors = validateReferralEmail(formData.email);
  if (emailErrors.length > 0) {
    errors.email = emailErrors;
  }

  const phoneErrors = validateReferralPhone(formData.phone);
  if (phoneErrors.length > 0) {
    errors.phone = phoneErrors;
  }

  const dateOfBirthErrors = validateDateOfBirth(formData.date_of_birth);
  if (dateOfBirthErrors.length > 0) {
    errors.date_of_birth = dateOfBirthErrors;
  }

  const conditionsErrors = validateHealthConditions(formData.conditions);
  if (conditionsErrors.length > 0) {
    errors.conditions = conditionsErrors;
  }

  const notesErrors = validateAdditionalNotes(formData.additional_notes);
  if (notesErrors.length > 0) {
    errors.additional_notes = notesErrors;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get first error message for a field
 */
export const getFirstFieldError = (
  errors: ValidationErrors,
  fieldName: keyof SelfReferralFormData
): string | undefined => {
  const fieldErrors = errors[fieldName];
  return fieldErrors && fieldErrors.length > 0 ? fieldErrors[0] : undefined;
};

/**
 * Check if a specific field has errors
 */
export const hasFieldError = (
  errors: ValidationErrors,
  fieldName: keyof SelfReferralFormData
): boolean => {
  const fieldErrors = errors[fieldName];
  return fieldErrors !== undefined && fieldErrors.length > 0;
};

/**
 * Sanitise form data by trimming strings and filtering invalid conditions
 */
export const sanitiseFormData = (formData: SelfReferralFormData): SelfReferralFormData => {
  return {
    first_name: formData.first_name?.trim() || '',
    last_name: formData.last_name?.trim() || '',
    email: formData.email?.trim() || '',
    phone: formData.phone ? {
      countryCode: formData.phone.countryCode?.trim() || '',
      isoCode: formData.phone.isoCode?.trim() || '',
      number: formData.phone.number?.trim() || ''
    } : undefined,
    date_of_birth: formData.date_of_birth?.trim() || undefined,
    conditions: formData.conditions?.filter(condition =>
      typeof condition === 'string' && condition.trim().length > 0
    ) || [],
    additional_notes: formData.additional_notes?.trim() || undefined
  };
};

/**
 * Create validation result for a single field
 */
export const createFieldValidationResult = (
  fieldName: keyof SelfReferralFormData,
  value: unknown
): ValidationResult => {
  const fieldErrors = validateField(fieldName, value);

  return {
    isValid: fieldErrors.length === 0,
    errors: fieldErrors.length > 0 ? { [fieldName]: fieldErrors } : {}
  };
};

/**
 * Export validation constants for testing
 */
export const REFERRAL_VALIDATION_CONSTANTS = {
  NAME_REGEX,
  DATE_REGEX,
  REFERRAL_ERRORS,
  MAX_NAME_LENGTH: 100,
  MIN_NAME_LENGTH: 2,
  MAX_CONDITIONS: 10,
  MAX_NOTES_LENGTH: 1000,
  MIN_YEAR: 1900,
  MAX_AGE: 120
} as const;